import React, { useEffect, useCallback, useMemo } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Cell } from 'react-table';

import { useParentSizeContext, ColumnWithSorting, Loading, Table } from '@tradingblock/components';
import { useDispatcher } from '../../../../data/global/hooks';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { AdminEntitlementsActions } from '../../../../data/global/actions/admin/EntitlementsActions';
import { EntitlementWithKey } from '@tradingblock/types';
import { useEntitlementsData } from '../useEntitlements';
import { EntitlementsActionCell } from './table/EntitlementsActionCell';
import { EntitlementsItemCell } from './table/EntitlementsItemCell';
import { AdminEntitlementsDetail } from '../AdminEntitlementsDetail';
import { useBlockId } from '../../../../components/blocks/BlockState';

const AdminEntitlementsColumns: ColumnWithSorting<EntitlementWithKey>[] = [
  {
    Header: 'Account Number',
    accessor: v => v.accountNumber || '',
    id: 'AccountNumber',
  },
  {
    Header: 'User ID',
    accessor: v => v.userId,
    id: 'UserId',
  },
  {
    Header: 'Name',
    accessor: v => v.name,
    id: 'AccountName',
    Cell: ({ row }: Cell<EntitlementWithKey>) => {
      const { name } = row.original;

      if (!name) {
        return <></>;
      } else {
        return <>{name}</>;
      }
    },
  },
  {
    Header: 'Request',
    id: 'item',
    Cell: ({ row }: Cell<EntitlementWithKey>) => {
      const {
        status,
        requestedMarketDataEntitlementType,
      } = row.original;
      return (
        <>
          <EntitlementsItemCell
            status={status}
            requestedMarketDataEntitlementType={requestedMarketDataEntitlementType}
          />
        </>
      );
    },
  },
  {
    Header: 'Date',
    accessor: v => v.requestDateTime,
    sortDescFirst: true,
    id: 'Date',
    Cell: ({ row }: Cell<EntitlementWithKey>) => {
      const dateVal = dayjs(row.original.requestDateTime);

      if (dateVal && dateVal.isValid()) {
        return (
          <>
            {dateVal.format('MM/DD/YYYY')}
            <br />
            {dateVal.format('h:mm A')}
          </>
        );
      }
      return <></>;
    },
  },
  {
    Header: 'Action',
    id: 'action',
    className: 'text-center',
    Cell: ({ row }: Cell<EntitlementWithKey>) => {
      return (
        <div className="text-center">
          <EntitlementsActionCell entitlementId={row.original.id} />
        </div>
      );
    },
  },
];

export const EntitlementsTable: React.FC<{}> = () => {
  const { dispatch } = useDispatcher();
  const blockId = useBlockId();

  const { data, total, page, pageSize } = useEntitlementsData(blockId);

  const selectedEntitlementId = useStateSelector(s =>
    s.private.entitlements.entitlementDataByBlockId &&
    s.private.entitlements.entitlementDataByBlockId[blockId] &&
    s.private.entitlements.entitlementDataByBlockId[blockId].selectedEntitlementId);
  const isFetching = useStateSelector(s =>
    s.private.entitlements.entitlementDataByBlockId &&
    s.private.entitlements.entitlementDataByBlockId[blockId] &&
    s.private.entitlements.entitlementDataByBlockId[blockId].isFetching);

  useEffect(() => {
    dispatch(AdminEntitlementsActions.requestEntitlementUpdates({ page, pageSize, blockId }));

    const intervalId = setInterval(function() {
      dispatch(AdminEntitlementsActions.refreshEntitlements({ page, pageSize, blockId }));
    }, 60 * 3 * 1000);
  
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const initialState = useMemo(() => {
    return {
      sortBy: [],
      pageSize: _.isUndefined(pageSize) ? 10 : pageSize,
      pageIndex: page || 0,
      columnOrder: [],
    };
  }, [pageSize, page]);

  const pageCount = useMemo(() => {
    if (pageSize && total) {
      return Math.ceil(total / pageSize);
    }
    return 0;
  }, [pageSize, total]);

  const onPage = useCallback(
    (page: number) => {
      dispatch(AdminEntitlementsActions.setEntitlementsPage({ page, blockId }));
    },
    [dispatch]
  );

  const onPageSize = useCallback(
    (pageSize: number) => {
      dispatch(AdminEntitlementsActions.setEntitlementsPageSize({ pageSize, blockId }));
    },
    [dispatch]
  );

  const getRowKey = useCallback((data: EntitlementWithKey) => `${data.entityKey}`, []);

  if (selectedEntitlementId) {
    return <AdminEntitlementsDetail />;
  }

  return (
    <>
      {(isFetching || isFetching === null || isFetching === undefined) && <Loading />}

      <useParentSizeContext.Provider>
        {isFetching === false && data && data.length > 0 && (
          <Table
            tableKey="admin-entitlements-table"
            columns={AdminEntitlementsColumns}
            numColumns={6}
            initialState={initialState}
            data={data}
            loaded={true}
            getRowKey={getRowKey}
            hasPagination
            manualPagination
            pageCount={pageCount}
            onPage={onPage}
            onPageSize={onPageSize}
          />
        )}
      </useParentSizeContext.Provider>
    </>
  );
};
